import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { DateExPipe, dotPathValueGetter } from "@modules/common";
import { AppQuery } from "@modules/common/app.store";
import { CellClickHandler, SortOrder, TableColumn, TableComponent } from "@modules/common/components";
import { DataTableSuper } from "@modules/common/components/data-table-super";
import { AuthenticationService, JobJobMachinesDto, JobService, MachineService, ProductSettingService, SnackBarService } from "@modules/common/services";
import { ListResultDto, LookupListEx, LookupObjectDto, SearchDto } from "@modules/models";
import { NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import { Observable } from "rxjs";
import { RulesRunnerService } from '@app/services/rules-runner.service';

type StatusType = "all" | "open" | "closed";

interface DisplayJobJobMachinesDto extends JobJobMachinesDto {
  selected?: boolean;
}

type TypeaheadSelectedEvent = NgbTypeaheadSelectItemEvent<LookupObjectDto> | {item: {code: string}};
@Component({
  selector: "abi-exchange-list",
  templateUrl: "./exchange-list.component.html",
  // styleUrls: ["./exchange-list.component.scss"],
})
export class ExchangeListComponent
  extends DataTableSuper<JobJobMachinesDto>
  implements OnInit
{
  canEdit = false;

  jobActiveFilters: LookupListEx<LookupObjectDto> = new LookupListEx<LookupObjectDto>([
    { code: "all", description: "Job.AllJobs", active: true },
    { code: "open", description: "Job.OpenJobs", active: true },
    { code: "closed", description: "Job.ClosedJobs", active: true },
  ]);
  defaultJobActiveFilter: StatusType = "open";
  defaultJobStatusFilter: string = "EXCHANGE";
  jobStatusFilter: string = null;
  jobActiveFilter: StatusType = null;
  jobHoldFilter: string = "";
  jobHoldClearedFilter: string = "";

  get baseColumns(): TableColumn[] {
    return [
      {
        field: "jobId",
        columnId: "job.jobId",
        header: "Job.JobID",
        group: "Job.Job",
        sortOrder: SortOrder.Ascending,
        defaultSort: SortOrder.Ascending,
        class: "w",
        thClass: "code-8",
        static: true
      },
      {
        field: "resourceId",
        columnId: "job.resourceId",
        header: "Job.Resource",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "code-8",
        // static: true
      },
      {
        field: "ourRef",
        columnId: "job.ourReferenceNumber",
        header: "Job.OurReference",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "",
        // static: true
      },
      {
        field: "field1",
        columnId: "job.field1",
        header: "Job.Field1",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        thClass: "code-8",
      },


      {
        field: "return.sequence",
        header: "Exchange.Sequence",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "code-6",
        hidden: true
      },
      {
        field: "return.action",
        header: "Exchange.Action",
        group: "Return",
        defaultSort: SortOrder.None,
        class: "",
        thClass: "code-5",
        skipFilter: true,

      },
      {
        field: "return.itemId",
        header: "Exchange.Model",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        thClass: "",
      },
      {
        field: "return.serialNumber",
        header: "Exchange.Serial",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        thClass: "",
      },
      {
        field: "return.warehouseId",
        header: "Exchange.WarehouseID",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        thClass: "",
        hidden: true
      },
      {
        field: "return.binId",
        header: "Exchange.BinID",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        thClass: "",
        hidden: true
      },

      // MACHINE CUSTOM FIELDS
      {
        field: "return.fields.0",
        header: "Machine.Field1",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Return",
      },
      {
        field: "return.fields.1",
        header: "Machine.Field2",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Return",
      },
      {
        field: "return.fields.2",
        header: "Machine.Field3",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Return",
      },
      {
        field: "return.fields.3",
        header: "Machine.Field4",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Return",
      },
      {
        field: "return.fields.4",
        header: "Machine.Field5",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Return",
      },


      // {
      //   field: "return.fields",
      //   header: "Exchange.Fields",
      //   group: "Return",
      //   defaultSort: SortOrder.Ascending,
      //   thClass: "",
      //   hidden: true
      // },
      {
        field: "return.ordered",
        header: "OrderLine.QuantityOrderedShort",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        thClass: "",
        hidden: true
      },
      {
        field: "return.requested",
        header: "Exchange.Request",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "date",
        pipe: new DateExPipe(), // Format the date as medium
      },
      {
        field: "return.delivered",
        header: "Exchange.Delivered",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "date",
        pipe: new DateExPipe(), // Format the date as medium
        hidden: true
      },
      {
        field: "return.by",
        header: "Exchange.By",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        thClass: "",
        hidden: true
      },
      {
        field: "return.printed",
        header: "Exchange.Printed",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "return.deliveryNote",
        header: "Exchange.DeliveryNote",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "return.closed",
        header: "Exchange.Closed",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "return.ediId",
        header: "Exchange.EdiID",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },

      // REPLACE
      {
        field: "replace.sequence",
        header: "Exchange.Sequence",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "code-6",
        hidden: true
      },
      {
        field: "replace.action",
        header: "Exchange.Action",
        group: "Replace",
        defaultSort: SortOrder.None,
        class: "",
        thClass: "code-5",
        skipFilter: true,
      },
      {
        field: "replace.itemId",
        header: "Exchange.Model",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
      },
      {
        field: "replace.serialNumber",
        header: "Exchange.Serial",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
      },
      {
        field: "replace.warehouseId",
        header: "Exchange.WarehouseID",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "replace.binId",
        header: "Exchange.BinID",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },

      // MACHINE CUSTOM FIELDS
      {
        field: "replace.fields.0",
        header: "Machine.Field1",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Replace",
      },
      {
        field: "replace.fields.1",
        header: "Machine.Field2",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Replace",
      },
      {
        field: "replace.fields.2",
        header: "Machine.Field3",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Replace",
      },
      {
        field: "replace.fields.3",
        header: "Machine.Field4",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Replace",
      },
      {
        field: "replace.fields.4",
        header: "Machine.Field5",
        defaultSort: SortOrder.Ascending,
        hidden: true,
        group: "Replace",
      },
      {
        field: "replace.ordered",
        header: "OrderLine.QuantityOrderedShort",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "replace.requested",
        header: "Exchange.Request",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "date",
        pipe: new DateExPipe(), // Format the date as medium
        hidden: true
      },
      {
        field: "replace.delivered",
        header: "Exchange.Delivered",
        group: "Return",
        defaultSort: SortOrder.Ascending,
        class: "",
        thClass: "date",
        pipe: new DateExPipe(), // Format the date as medium
        hidden: true
      },
      {
        field: "replace.by",
        header: "Exchange.By",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
      },
      {
        field: "replace.printed",
        header: "Exchange.Printed",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "replace.deliveryNote",
        header: "Exchange.DeliveryNote",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "replace.closed",
        header: "Exchange.Closed",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: true
      },
      {
        field: "replace.ediId",
        header: "Exchange.EdiID",
        group: "Replace",
        defaultSort: SortOrder.Ascending,
        class: "",
        hidden: false
      },

      {
        field: "action",
        header: "",
        group: "Job.Job",
        defaultSort: SortOrder.None,
        class: "",
        thClass: "code-3",
        static: true
      },
    ];
  }

  resourceColumns = [
    "job.resourceId"
  ];
  dateColumns: string[] = [
    "return.requested",
    "replace.requested",
  ];

  constructor(
    route: ActivatedRoute,
    private router: Router,
    private jobService: JobService,
    private appQuery: AppQuery,
    private authService: AuthenticationService,
    private productSettings: ProductSettingService,
    private machineService: MachineService,
    private snackBar: SnackBarService,
    private rulesRunnerService: RulesRunnerService // Inject the service here
  ) {
    super("JobExchangeList", route);
  }

  setupColumns(): void {
    this.selectedStockSerials = {};
    const extraColumns = [];
    this.columns = [...this.baseColumns, ...extraColumns];
  }

  replenishWarehouseId = "";
  ngOnInit() {
    this.exchangeSelectionRules = this.productSettings.stringValue("Exchange.SelectionRules") || "";
    this.replenishWarehouseId = this.productSettings.stringValue("Exchange.ReplenishWarehouseId") || "";
    this.defaultJobStatusFilter = this.productSettings.stringValue("Exchange.DefaultJobStatusFilter") || this.defaultJobStatusFilter;
    this.defaultJobActiveFilter = this.productSettings.stringValue("Exchange.DefaultJobActiveFilter") as StatusType ||this.defaultJobActiveFilter;
    this.showExportButton = this.appQuery.hasRoles(this.productSettings.arrayValue("Exchange.ExportLinesRoles", ["*"]));
    this.showReplenishButton = this.appQuery.hasRoles(this.productSettings.arrayValue("Exchange.ReplenishRoles", ['MAN', 'ADMIN']));
    this.setupTable(true);
  }

  safeEncode(val: string) {
    return this.jobService.safeEncode(val);
  }

  innerSearch(
    pageNumber: number,
    pageSize: number,
    columns: TableColumn[]
  ): Observable<ListResultDto<JobJobMachinesDto>> {
    const search: SearchDto = this.newSearch(pageNumber, pageSize, columns);
    this.applyFilters(search, columns);

    switch (this.jobActiveFilter) {
      case "open":
        search.filters['job.closed'] = "false";
        break;
      case "closed":
        search.filters['job.closed'] = "true";
        break;
    }

    if(this.jobStatusFilter)
      search.filters['job.statusId'] = this.jobStatusFilter;

    if(this.jobHoldFilter)
      search.filters['job.hold'] = this.jobHoldFilter;

    if(this.jobHoldClearedFilter)
      search.filters['job.clearedhold'] = this.jobHoldClearedFilter;

    return this.jobService.searchExchangeLines(search);
  }


  prependHoldLookups: LookupObjectDto[] = [
    {
      code: 'hold',
      description: 'Any Hold',
      active: true
    },
    {
      code: 'nohold',
      description: 'No Hold',
      active: true
    }
  ]
  jobStatusFilterChange(event: TypeaheadSelectedEvent) {
    this.router.navigate(['./', { clearedhold: this.jobHoldClearedFilter, jobHold: this.jobHoldFilter,  jobStatus: event.item.code, jobActive: this.jobActiveFilter}], {relativeTo: this.route });
  }
  jobHoldFilterChange(event: TypeaheadSelectedEvent) {
    this.router.navigate(['./', { clearedhold: this.jobHoldClearedFilter, jobHold: event.item.code, jobStatus: this.jobStatusFilter, jobActive: this.jobActiveFilter}], {relativeTo: this.route });
  }
  jobHoldClearedFilterChange(event: TypeaheadSelectedEvent) {
    this.router.navigate(['./', { clearedhold: event.item.code, jobHold: this.jobHoldFilter, jobStatus: this.jobStatusFilter, jobActive: this.jobActiveFilter}], {relativeTo: this.route });
  }
  // AKA: Job Active Filter
  setStatus(status: StatusType) {
    this.router.navigate(['./', { clearedhold: this.jobHoldClearedFilter, jobHold: this.jobHoldFilter,  jobActive: status, jobStatus: this.jobStatusFilter }], {relativeTo: this.route });
  }

  extraVars(params: ParamMap): void {
    super.extraVars(params);// maybe, maybe not
    this.jobActiveFilter = params.get('jobActive') as StatusType || this.defaultJobActiveFilter;
    this.jobStatusFilter = params.get('jobStatus') || this.defaultJobStatusFilter;
    this.jobHoldFilter = params.get('jobHold') || '';
    this.jobHoldClearedFilter = params.get('clearedhold') || '';
  }

  /**
   * Navigate without Params
   */
  resetFilters() {
    // this.selectedStockSerials = {};
    this.router.navigate(['./', {}], {relativeTo: this.route });
  }


  /// SELECTION
  private getKeyFromLine(s: DisplayJobJobMachinesDto) {
    return s.return.itemId + "||" + s.return.serialNumber + "||" + s.return.binId;
  }

  selectedStockSerials: { [key: string]: number } = {};
  toggleSelected(s: DisplayJobJobMachinesDto) {
    // s.selected = !s.selected;
    // const stockBin = s.parent;
    const key = this.getKeyFromLine(s);
    this.selectedStockSerials[key] = !this.isSelected(s) ? 1 : 0;
  }

  // TODO: Need to expose the job.status field in data
  // exchangeSelectionRules = "job.status=FINEX|return.closed=1|replace.closed=1|!replace.ediId";
  exchangeSelectionRules = "";
  // exchangeSelectionRules = "!replace.ediId";
  rulesRunner(row: DisplayJobJobMachinesDto){
    return this.rulesRunnerService.runRules(row, this.exchangeSelectionRules);
  }

  canSelect(row: DisplayJobJobMachinesDto): boolean {
    return this.rulesRunner(row);
  }

  isSelected(s: DisplayJobJobMachinesDto): boolean {
    const key = this.getKeyFromLine(s);
    return this.selectedStockSerials[key] === 1;
  }

  toggleAll() {
    const checked = !this.allSelected();
    this.dataList.items.forEach((s) => {
      // s.selected = checked;
      const key = this.getKeyFromLine(s);
      this.selectedStockSerials[key] = this.canSelect(s) && checked ? 1 : 0;
    });
  }

  allSelected(): boolean {
    return this.dataList?.items.every((s) => {
      const key = this.getKeyFromLine(s);
      return this.selectedStockSerials[key] === 1;
    });
  }

  canReplenish()
  {
    return this.selectectedCount() > 0;
  }

  selectectedCount() {
    return Object.values(this.selectedStockSerials).reduce((a, b) => a + b, 0);
  }

  apiBusy = false;
  onReplenish() {
    const selected = Object.keys(this.selectedStockSerials).filter((k) => this.selectedStockSerials[k] === 1);
    this.apiBusy = true;
    const selectedJobIdlist = this.dataList.items.reduce((pv, cv, i) => selected.includes(this.getKeyFromLine(cv)) ? pv.concat(cv.jobId) : pv, []);
    this.jobService.execJobSalesOrder(selectedJobIdlist).subscribe(res => {
      this.snackBar.info(`Replenishment Request Sent for Jobs: ${selectedJobIdlist.join(', ')}`);
    }, (error) => {
      this.snackBar.danger('Failed Request');
    }, () => {
      this.apiBusy = false;
      this.triggerRefresh();
    });
  }

  onSort(evt){
    this.resetSelection();
  }

  onFilteredColumns(evt) {
    this.resetSelection();
  }

  resetSelection(){
    this.selectedStockSerials = {};
  }

  onCellClick(event: CellClickHandler<JobJobMachinesDto>, table: TableComponent) {
    return;
    const column = event.column;

    // MUCH THAT CAN BE DONE HERE....

    // APPLY A FILTER FROM THE ROW VALUE
    if(!column.skipFilter)
      table.filter(event.column, dotPathValueGetter(event.row, event.column.field || event.column.columnId));

    // SELECT THE ITEM
    // event.column.columnId
    // console.log(event);
    // if (event.rowIndex === -1) return;
    // if (!this.selectedStockSerials[event.data.serialNumber]) {
    //   this.selectedStockSerials[event.data.serialNumber] = 1;
    //   // this.table.toggleRowSelection(event.data, true);
    // } else {
    //   delete this.selectedStockSerials[event.data.serialNumber];
    //   // this.table.toggleRowSelection(event.data, false);
    // }
    // console.log(this.selectedStockSerials);
  }

  get numberOfSelections() {
    return Object.keys(this.selectedStockSerials).length;
  }

  showReplenishButton = false;
  showExportButton = false;

  /**
   * Custom TableData Exporter
   */
  exportTable(table: TableComponent): any[][] {
    const selected = Object.keys(this.selectedStockSerials).filter((k) => this.selectedStockSerials[k] === 1);
    return table.getTableData(false, true, (row, index) => {
      return selected.includes(this.getKeyFromLine(row));
    });
  }

}
